import { jwtDecode } from "jwt-decode";
import { apiGatewayClient } from "../api/clients";

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

const STORAGE_KEY = "auth_tokens";
const REFRESH_THRESHOLD_SECONDS = 5 * 60;

let refreshPromise: Promise<AuthTokens | null> | null = null;

export function getStoredTokens(): AuthTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as AuthTokens;
  } catch {
    return null;
  }
}

export function setStoredTokens(tokens: AuthTokens): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens));
}

export function clearStoredTokens(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function isTokenExpired(token: string): boolean {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

function isTokenExpiringSoon(token: string): boolean {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    return Date.now() >= (exp - REFRESH_THRESHOLD_SECONDS) * 1000;
  } catch {
    return true;
  }
}

async function refreshTokens(refreshToken: string): Promise<AuthTokens | null> {
  try {
    const response = await apiGatewayClient.post<{
      payload: { idToken: string; accessToken: string };
    }>("/auth/refresh-session", { refreshToken });

    const { idToken, accessToken } = response.data.payload;
    const updated: AuthTokens = { idToken, accessToken, refreshToken };
    setStoredTokens(updated);
    return updated;
  } catch {
    clearStoredTokens();
    return null;
  }
}

export async function getValidToken(): Promise<string | null> {
  const tokens = getStoredTokens();
  if (!tokens) return null;

  if (!isTokenExpiringSoon(tokens.idToken)) {
    return tokens.idToken;
  }

  if (!refreshPromise) {
    refreshPromise = refreshTokens(tokens.refreshToken).finally(() => {
      refreshPromise = null;
    });
  }

  const refreshed = await refreshPromise;
  return refreshed?.idToken ?? null;
}
